"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { signOut, useSession } from "next-auth/react";
import { Trans } from "@lingui/react/macro";
import UserSvg from "@/components/svg/header/user";
import WalletSvg from "@/components/svg/header/wallet";
import BagSvg from "@/components/svg/header/bag";
import CashSvg from "@/components/svg/header/cash";
import HeartSvg from "@/components/svg/header/heart";
import LogoutSvg from "@/components/svg/header/logout";
import BellSvg from "@/components/svg/header/bell";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { getRequest } from "@/utils/request";
import useLocalizedRouter from "@/hooks/use-localized-router";
import { useNotificationStore } from "@/components/context/notification-store";
import {
  GUEST_CART_MERGE_KEY,
  GUEST_LOGIN_TRANSITION_COOKIE,
  GUEST_LOGIN_TRANSITION_KEY,
  SESSION_FLAG_KEY,
  USER_TOKEN_KEY,
} from "@/components/core/session-monitor";
import { useXNoticeStore } from "@/components/context/xnotice-store";
import OfferzPlusSvg from "@/components/svg/offerz-plus";

interface WishlistProps {
  product_id: string;
  product_seo_url: string;
  thumb: string;
  name: string;
  model: string;
  stock: string;
  price: string;
  special: string;
}

const menuItems = [
  { label: <Trans>My account</Trans>, path: "/account", Icon: UserSvg, isWishlist: false },
  {
    label: <Trans>Notifications</Trans>,
    path: "/notifications",
    Icon: BellSvg,
    isWishlist: false,
    isNotification: true,
  },
  {
    label: <Trans>My Wallet</Trans>,
    path: "/account/voucherwallet",
    Icon: WalletSvg,
    isWishlist: false,
  },
  { label: <Trans>Order History</Trans>, path: "/account/orders", Icon: BagSvg, isWishlist: false },
  {
    label: <Trans>Transactions</Trans>,
    path: "/account/transactions",
    Icon: CashSvg,
    isWishlist: false,
  },
  { label: <Trans>Offerz Plus</Trans>, path: "/loyalty", Icon: OfferzPlusSvg, isWishlist: false },
  { label: <Trans>Wish List</Trans>, path: "/account/wishlist", Icon: HeartSvg, isWishlist: true },
];

export default function HeaderUserMenu() {
  const [pendingToggle, setPendingToggle] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const isCountLoading = useNotificationStore((state) => state.isCountLoading);
  const { data: session, status } = useSession();
  const router = useLocalizedRouter();
  const lang = Cookies.get("locale");
  const queryClient = useQueryClient();
  const { setCartCategoryIDs, setCartProductIDs } = useXNoticeStore();

  const resetClientCartState = useCallback(() => {
    setCartCategoryIDs([]);
    setCartProductIDs([]);
    queryClient.setQueriesData(
      { queryKey: ["cartQuantity"] },
      {
        success: 1,
        data: { quantity: 0, category_ids: [], product_ids: [] },
      },
    );
    queryClient.setQueryData(["cart"], {
      success: 1,
      data: {
        products: [],
        totals: [],
        total: 0,
        total_raw: 0,
        total_product_count: 0,
        total_loyalty_points: 0,
        text_items: 0,
        has_shipping: 0,
        has_download: 0,
        vouchers: [],
        coupon_status: 1,
        coupon: null,
        voucher_status: 1,
        voucher: null,
        category_ids: [],
        product_ids: [],
      },
    });
  }, [queryClient, setCartCategoryIDs, setCartProductIDs]);

  const getBadgeSizeClass = (count: number) =>
    count > 999 ? "h-[18px] w-[34px]" : count > 99 ? "h-[18px] w-[28px]" : "h-[18px] w-[18px]";

  const outerBadgeClasses = `absolute text-xs right-0 top-0 flex items-center justify-center rounded-full bg-[#0097ab] text-center text-white ${
    unreadCount > 999
      ? "h-[18px] w-[34px] !right-[-12px]"
      : unreadCount > 99
        ? "h-[18px] w-[28px] !right-[-5px]"
        : "h-[18px] w-[18px]"
  }`;

  const menuBadgeClasses = `ml-2 inline-flex items-center justify-center rounded-full bg-[#0097ab] px-1.5 text-xs text-white ${getBadgeSizeClass(unreadCount)}`;

  useEffect(() => {
    const dataFetch = async () => {
      try {
        const response = await getRequest({
          path: { name: "wishlist" },
          lang,
          skipCache: true,
        });

        if (response?.data && Array.isArray(response.data)) {
          const wishlistArray = response.data.map((product: WishlistProps) => product.product_id);
          if (status === "unauthenticated") {
            Cookies.set("anon-wishlist", JSON.stringify(wishlistArray));
          } else {
            Cookies.set("wishlist", JSON.stringify(wishlistArray));
          }
        }
      } catch (err) {
        console.warn("Error fetching wishlist:", err);
      }
    };
    dataFetch();
  }, [lang, status]);

  const handleToggle = () => {
    if (status === "loading") {
      setPendingToggle(true);
    } else {
      handleSession();
    }
  };

  const handleSession = useCallback(async () => {
    if (session && session.user?.is_guest === "1") {
      localStorage.setItem(GUEST_LOGIN_TRANSITION_KEY, "1");
      localStorage.setItem(GUEST_CART_MERGE_KEY, "1");
      Cookies.set(GUEST_LOGIN_TRANSITION_COOKIE, "1", { path: "/", sameSite: "lax" });
      localStorage.removeItem(SESSION_FLAG_KEY);
      localStorage.removeItem(USER_TOKEN_KEY);
      resetClientCartState();
      await signOut({ callbackUrl: router.localizeHref("/login"), redirect: true });
    } else {
      if (!session || (session && new Date(session.expires) < new Date())) {
        router.push("/login");
      } else {
        setMenuOpen((prev) => !prev);
      }
    }
  }, [resetClientCartState, router, session]);

  useEffect(() => {
    if (pendingToggle && status !== "loading") {
      setPendingToggle(false);
      handleSession();
    }
  }, [pendingToggle, status, session, router, handleSession]);

  const wishlist = Cookies.get("wishlist");
  const wishlistArray = wishlist ? JSON.parse(wishlist) : [];

  const handleMenuItemClick = (path: string) => {
    router.push(path);
    setMenuOpen(false);
  };

  const handleLogout = async () => {
    localStorage.removeItem(SESSION_FLAG_KEY);
    const capturedAppToken = localStorage.getItem(USER_TOKEN_KEY);
    localStorage.removeItem(USER_TOKEN_KEY);
    resetClientCartState();
    try {
      await fetch("/api/auth/backend-logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appToken: capturedAppToken, lang }),
      });
    } catch {
      // proceed with client-side logout regardless
    }
    await signOut({ callbackUrl: router.localizeHref("/signout") });
  };

  // Close the dropdown when clicking outside
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="relative inline-flex">
      <div
        onClick={handleToggle}
        className="relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg md:h-[42px] md:w-[42px] md:hover:bg-[#eeeef0]"
      >
        <UserSvg />
        {status === "authenticated" && isCountLoading ? (
          <span className="absolute right-0 top-0 h-[18px] w-[18px] animate-pulse rounded-full bg-gray-300"></span>
        ) : status === "authenticated" && unreadCount > 0 ? (
          <div className={outerBadgeClasses}>{unreadCount > 999 ? "999+" : unreadCount}</div>
        ) : null}
      </div>

      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 z-10 mt-10 min-w-[215px] rounded border border-gray-200 bg-white shadow-lg"
        >
          <ul>
            {menuItems
              .filter(({ path }) => {
                if (
                  path === "/loyalty" &&
                  session?.user &&
                  parseInt(session?.user?.customer_group_id) ===
                    Number(process.env.NEXT_PUBLIC_BUSSINESS_CUSTOMER_GROUP)
                ) {
                  return false;
                }
                return true;
              })
              .map(({ label, path, Icon, isWishlist, isNotification }, index) => (
                <li
                  key={index}
                  onClick={() => handleMenuItemClick(path)}
                  className="flex w-full cursor-pointer items-center justify-between border-b border-[#E0e0e0] p-[14px] hover:text-primary"
                >
                  <div className="flex min-w-0 flex-1 items-center">
                    <div className="min-w-8">
                      <Icon />
                    </div>
                    <span className="block min-w-0 whitespace-nowrap pr-2 text-[17px] leading-5 transition-all duration-300 ease-in-out">
                      {label}
                      {isWishlist ? ` (${wishlistArray.length})` : ""}
                    </span>
                  </div>
                  {isNotification && unreadCount > 0 ? (
                    <span className={`${menuBadgeClasses} shrink-0`}>
                      {unreadCount > 999 ? "999+" : unreadCount}
                    </span>
                  ) : null}
                </li>
              ))}
            <li
              onClick={handleLogout}
              className="flex cursor-pointer items-center p-[14px] hover:text-primary"
            >
              <div className="mr-2">
                <LogoutSvg />
              </div>
              <span className="text-[17px]">
                <Trans>Logout</Trans>
              </span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
